export type QuoteColumnType = "system" | "custom";

export type QuoteColumnKey =
  | "module"
  | "item"
  | "unit"
  | "days"
  | "quantity"
  | "rate"
  | "amount"
  | "status"
  | "notes"
  | (string & {});

export type QuoteColumn = {
  key: QuoteColumnKey;
  labelEn: string;
  labelZh: string;
  visible: boolean;
  sortOrder: number;
  type: QuoteColumnType;
};

const systemColumnKeys = new Set(["module", "item", "unit", "days", "quantity", "rate", "amount", "status", "notes"]);

export const DEFAULT_QUOTE_COLUMNS: QuoteColumn[] = [
  { key: "module", labelEn: "ITEM", labelZh: "项目", visible: true, sortOrder: 10, type: "system" },
  { key: "item", labelEn: "Description", labelZh: "描述", visible: true, sortOrder: 20, type: "system" },
  { key: "unit", labelEn: "UNIT", labelZh: "单位", visible: true, sortOrder: 30, type: "system" },
  { key: "days", labelEn: "DAYS", labelZh: "天数", visible: true, sortOrder: 40, type: "system" },
  { key: "quantity", labelEn: "NUM", labelZh: "数量", visible: true, sortOrder: 50, type: "system" },
  { key: "rate", labelEn: "RATE", labelZh: "单价", visible: true, sortOrder: 60, type: "system" },
  { key: "amount", labelEn: "TOTAL COST", labelZh: "总价", visible: true, sortOrder: 70, type: "system" },
  { key: "status", labelEn: "TERMS", labelZh: "计价方式", visible: true, sortOrder: 80, type: "system" },
  { key: "notes", labelEn: "Notes", labelZh: "备注", visible: false, sortOrder: 90, type: "system" },
];

export function normalizeQuoteColumns(columns: QuoteColumn[] | null | undefined): QuoteColumn[] {
  const byKey = new Map(DEFAULT_QUOTE_COLUMNS.map((column) => [column.key, { ...column }]));

  for (const column of columns ?? []) {
    const normalized = normalizeColumn(column);

    if (!normalized) {
      continue;
    }

    byKey.set(normalized.key, normalized);
  }

  return Array.from(byKey.values()).sort((left, right) => left.sortOrder - right.sortOrder || left.key.localeCompare(right.key));
}

export function getVisibleQuoteColumns(columns: QuoteColumn[]): QuoteColumn[] {
  return normalizeQuoteColumns(columns).filter((column) => column.visible);
}

export function isSystemQuoteColumn(key: string): boolean {
  return systemColumnKeys.has(key);
}

function normalizeColumn(column: QuoteColumn): QuoteColumn | null {
  const key = column.key.trim();
  const type = column.type === "custom" ? "custom" : "system";

  if (type === "system" && !isSystemQuoteColumn(key)) {
    return null;
  }

  const defaultColumn = DEFAULT_QUOTE_COLUMNS.find((candidate) => candidate.key === key);
  const labelEn = column.labelEn.trim() || defaultColumn?.labelEn;
  const labelZh = column.labelZh.trim() || defaultColumn?.labelZh;

  if (!labelEn || !labelZh) {
    return null;
  }

  return {
    key,
    labelEn,
    labelZh,
    visible: Boolean(column.visible),
    sortOrder: Number.isFinite(column.sortOrder) ? Math.trunc(column.sortOrder) : defaultColumn?.sortOrder ?? 1000,
    type,
  };
}
